import React from 'react';
import { connect } from 'react-redux';
import { history } from 'store';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    Line,
    LineChart,
    ResponsiveContainer,
} from 'recharts';
import {
    data,
    data1,
    data2,
} from './data';
import ChartCard from 'components/dashboard/Common/ChartCard';
import ContainerHeader from 'components/ContainerHeader/index';
import CircularProgress from 'components/CircularProgress';
import NextBestActions from '../Components/NextBestActions';
import ColorOptionsModal from 'components/colorOptionsModal';
import CardHeader from 'components/dashboard/Common/CardHeader/index';
import IntlMessages from 'util/IntlMessages';
import { getIdentityOrgId, getIdentityId } from 'services/Authentication';
import {
    getActiveSurveysCount,
    getInactiveSurveysCount,
    getActiveSurveys,
    getEmpSurveyAnswers,
    surveyActive,
    empSurveySummaryStatistics
} from 'services/SurveyService';
import { fetchOrg, showOrgLoader, fetchOrgStatistics } from 'actions/Organisation';
import { fetchEmpSurveyAnswers, showEmpLoader, showEmpDashboardLoader, hideEmpDashboardLoader } from 'actions/Employee';
import { fetchDataRequest } from 'services/ApiService';
import { formatPercentageRounded } from 'services/NumberFormatters';
import { isAdmin } from 'services/Authentication';
import { ORGID_PATH_PARAM, ORG_SURVEY_ANSWERS_ENDPOINT } from 'constants/api';

class EmpDashboard extends React.Component {

    onOptionMenuToggle = () => {
        this.setState({ menuState: !this.state.menuState });
    };
    handleRequestClose = () => {
        this.setState({ menuState: false });
    };

    constructor() {
        super();
        this.state = {
            menuState: false,
            nextBestActions: null,
            cardHeight: 110, // set to 60+ if you want the graphs to show on the dashboard (does take up room)
        };
    }

    counter = 0;

    createData(name, desc, image, color, action, component) {
        this.counter += 1;
        return { id: this.counter, name, desc, image, color, action, component };
    }

    componentDidMount() {
        const { showEmpDashboardLoader, fetchOrg, fetchOrgStatistics } = this.props;
        const orgid = getIdentityOrgId();
        showEmpDashboardLoader();
        fetchOrgStatistics(orgid);
        fetchOrg(orgid);
    }

    async componentDidUpdate(prevProps) {
        const { selectedOrg, hideEmpDashboardLoader } = this.props;
        if (selectedOrg !== prevProps.selectedOrg) {
            await this.getNextBestActions();
        }
        hideEmpDashboardLoader();
    }

    async getNextBestActions() {
        const { fetchEmpSurveyAnswers, showEmpLoader, selectedOrg } = this.props;
        const nextActions = [];
        const empId = getIdentityId();
        if (isAdmin()) {
            nextActions.push(
                this.createData(
                    'You are an administrator',
                    'You can make changes at the organisation level to maintain projects and project priorities, setup and administer surveys - goto the dashboard',
                    'account-box-mail',
                    'bg-light-blue darken-3',
                    () => {
                        console.log('Click admin org dashboard');
                        history.push('/app/org-dashboard/default');
                    }
                )
            );
        }

        // TODO convert to a single API call
        const activeSurveys = getActiveSurveys(selectedOrg ?.surveys);
        // CAREFUL - took me a while to work out the pitfalls of async/ await with forEach, etc
        // https://medium.com/dailyjs/the-pitfalls-of-async-await-in-array-loops-cf9cf713bfeb
        // basically forEach and async/ await - order not guaranteed. Promise.all but For ... of works but is the slowest option
        for (const orgSurvey of activeSurveys) {
            const answers = await
                fetchDataRequest(
                    ORG_SURVEY_ANSWERS_ENDPOINT,
                    [ORGID_PATH_PARAM, 'orgSurveyId'],
                    [selectedOrg ?.id, orgSurvey ?.id]
                );
            const empSurveyAnswers = getEmpSurveyAnswers(answers?.length > 0 ? answers[0] : answers, empId);
            // NOTE before first access the emp survey will equal [] - questions/ answers are injected only on first access
            if (empSurveyAnswers && surveyActive(empSurveyAnswers[0])) {
                const surveySummaryStatistics = empSurveySummaryStatistics(empSurveyAnswers[0]);
                nextActions.push(this.createData(
                    'You have a survey to complete',
                    `Survey ${orgSurvey.name} is only ${formatPercentageRounded(surveySummaryStatistics.percentCompleted)} complete. 
                    You have completed ${surveySummaryStatistics.questionsCompleted} out of ${surveySummaryStatistics.numberOfQuestions} questions.`,
                    'view-dashboard',
                    'bg-red accent-4',
                    () => {
                        console.log(`Click org survey id ${orgSurvey.id}`);
                        showEmpLoader();
                        fetchEmpSurveyAnswers({ orgSurveyId: orgSurvey.id, surveyId: orgSurvey.surveyId });
                        history.push('/app/org-emp-survey/default');
                    }
                ));
            }
        }
        nextActions.push(
            this.createData(
                'Account details',
                'You can make changes to your details like email, your name, your phone number.',
                'account-box-mail',
                'bg-light-blue darken-3',
                () => {
                    console.log('Click account details');
                    // return (<Redirect to={'/app/agile-dashboard/default'} />)
                }
            )
        );
        nextActions.push(
            this.createData(
                'Look and feel',
                'You can make changes to the look and feel of the application',
                'account-box-mail',
                'bg-light-blue darken-3',
                () => {
                    console.log('change look and feel clicked');
                    // return (<Redirect to={'/app/agile-dashboard/default'} />)
                },
                <ColorOptionsModal />
            )
        );
        this.setState({ nextBestActions: nextActions });
    }

    render() {
        const { selectedOrg, match, orgStatisticsData, loadingDashboard } = this.props;
        const { nextBestActions, cardHeight } = this.state;
        return (
            <div className="dashboard animated slideInUpTiny animation-duration-3">
                {loadingDashboard ?
                    <div
                        className="loader-view-block h-100">
                        <div className="loader-view">
                            <CircularProgress />
                        </div>
                    </div>
                    :
                    <React.Fragment>
                        <div style={{ marginLeft: '20px', marginTop: '20px' }}>
                            <ContainerHeader match={match} title={<IntlMessages id="emp.dashboard" />} />
                        </div>

                        <div className="row" style={{ marginLeft: '5px', marginRight: '5px', marginTop: '5px' }}>
                            <div className="col-lg-3 col-sm-6 col-12">
                                <ChartCard styleName="bg-secondary text-white">
                                    <div className="chart-title">
                                        <p><IntlMessages id="dashboard.currentOrganisation" /></p>
                                        <h2>{selectedOrg ? selectedOrg.name : null}</h2>
                                    </div>
                                    <ResponsiveContainer width="100%" height={cardHeight}>
                                        <BarChart data={data}>
                                            <Bar dataKey="pv" fill="white" />
                                            <Bar dataKey="uv" fill="white" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </ChartCard>
                            </div>

                            <div className="col-lg-3 col-sm-6 col-12">
                                <ChartCard styleName="bg-primary text-white">
                                    <div className="chart-title">
                                        <p><IntlMessages id="dashboard.numberOfInactiveSurveys" /></p>
                                        <h2>{getInactiveSurveysCount(selectedOrg ?.surveys)}</h2>
                                    </div>
                                    <ResponsiveContainer width="100%" height={cardHeight}>
                                        <AreaChart data={data1} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                                            <Area type="monotone" dataKey="pv" stroke="rgba(255,255,255,0.5)"
                                                activeDot={{ r: 8 }}
                                                fillOpacity={.5}
                                                fill="white" />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </ChartCard>
                            </div>

                            <div className="col-lg-3 col-sm-6 col-12">
                                <ChartCard styleName="bg-teal text-white">
                                    <div className="chart-title">
                                        <p><IntlMessages id="dashboard.numberOfActiveSurveys" /></p>
                                        <h2>{getActiveSurveysCount(selectedOrg ?.surveys)}</h2>
                                    </div>
                                    <ResponsiveContainer width="100%" height={cardHeight}>
                                        <LineChart data={data2}>
                                            <Line type="monotone" dataKey="uv" stroke="#ffffff" activeDot={{ r: 8 }} />
                                            <Line type="monotone" dataKey="pv" stroke="#ffffff" />
                                            <Line type="monotone" dataKey="amt" stroke="#ffffff" />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </ChartCard>
                            </div>

                            <div className="col-lg-3 col-sm-6 col-12">
                                <ChartCard styleName="bg-blue lighten-1 text-white">
                                    <div className="chart-title">
                                        <p><IntlMessages id="dashboard.numberOfProjects" /></p>
                                        <h2>{orgStatisticsData ?.projectCount || 0}</h2>
                                    </div>
                                    <ResponsiveContainer width="100%" height={cardHeight}>
                                        <LineChart data={data2}>
                                            <Line type="monotone" dataKey="uv" stroke="#ffffff" activeDot={{ r: 8 }} />
                                            <Line type="monotone" dataKey="pv" stroke="#ffffff" />
                                            <Line type="monotone" dataKey="amt" stroke="#ffffff" />
                                        </LineChart>
                                    </ResponsiveContainer>

                                </ChartCard>
                            </div>
                        </div>
                        <div className="row" style={{ marginLeft: '5px', marginRight: '5px' }}>
                            <div className="col-lg-12 col-sm-12 col-12">
                                <div className="jr-card">
                                    <CardHeader heading={<IntlMessages id="dashboard.nextBestActions" />} styleName="mb-2" />
                                    <NextBestActions actions={nextBestActions} />
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                }
            </div>

        );
    }
}

const mapStateToProps = ({ orgs, emps }) => {
    const {
        selectedOrg,
        orgStatisticsData,
    } = orgs;
    const { loadingDashboard } = emps;
    return {
        selectedOrg,
        orgStatisticsData,
        loadingDashboard,
    };
};
export default connect(mapStateToProps, {
    fetchOrg,
    showOrgLoader,
    showEmpLoader,
    showEmpDashboardLoader,
    hideEmpDashboardLoader,
    fetchEmpSurveyAnswers,
    fetchOrgStatistics,
})(EmpDashboard);



// WEBPACK FOOTER //
// ./components/Agile/EmpDashboard/index.js